import { formatWalletAmount } from '../../Wallet.money';
import type {
  tThresholdAmount,
  tThresholdBlockedBy,
  tThresholdBoundCode,
  tThresholdLimits,
  tThresholdTopUpState,
  tWalletBalance,
  tWalletCurrency,
} from '../../Wallet.types';
import type {
  tActiveThreshold,
  tPausedThreshold,
  tThresholdInfo,
  tTopUpPreset,
  tTopUpTypeBlocked,
} from './TopUpModal.types';

/**
 * The threshold top-up's form, without React (RET-2425, U5).
 *
 * Two jobs, both pure:
 *
 * - VALIDATION, in wallet-service's own order (`ValidateThresholdTopUp`,
 *   `internal/wallet/domain/topup_presets.go`): whole units, limit minimum,
 *   amount minimum, amount maximum, amount strictly above the limit. Every
 *   bound comes from `state.limits`, the figures wallet publishes in the GET,
 *   never from a constant here: a bound copied into the front is a second
 *   copy of the rule, and the day wallet moves it the two disagree.
 * - THE PROPS the presentational component reads (`tThresholdInfo` and the
 *   amount fields), with every money label already formatted.
 *
 * The texts of a bound live in ONE function, `thresholdBoundText`, which
 * takes the same `{code, limitMicros}` a 400 `invalid_amount` carries. The
 * client-side check and the server's answer for the same value therefore
 * print the same sentence.
 */

type tTranslate = (key: string, options?: Record<string, unknown>) => string;

const MICROS_PER_UNIT = 1_000_000n;

/** What the customer typed, or a preset, read as exact micro-units. */
export type tUnitsRead =
  | { status: 'empty' }
  | { status: 'invalid' }
  | { status: 'ok'; micros: bigint };

/**
 * Reads a typed amount in units. Fractions are READ, not rejected: whether
 * "9.5" is allowed is the validator's decision (`not_whole_units`), so the
 * customer learns the rule instead of seeing a generic "invalid".
 *
 * More than six decimals is not a money figure in this contract (micro-units),
 * so it is `invalid`, like anything that is not a decimal.
 */
export function readUnits(raw: string): tUnitsRead {
  const trimmed = raw.trim().replace(',', '.');

  if (trimmed === '') return { status: 'empty' };
  if (!/^\d+(\.\d{1,6})?$/.test(trimmed)) return { status: 'invalid' };

  const [whole, fraction = ''] = trimmed.split('.');

  return {
    status: 'ok',
    micros: BigInt(whole) * MICROS_PER_UNIT + BigInt(fraction.padEnd(6, '0')),
  };
}

/** A field the customer edits that is not even a number. Front-only. */
export type tThresholdFieldCode = tThresholdBoundCode | 'invalid_format';

export type tThresholdValidation = {
  /** The limit field's own error. */
  thresholdCode: Extract<
    tThresholdFieldCode,
    'not_whole_units' | 'threshold_below_min' | 'invalid_format'
  > | null;
  /** The amount's own error. `amount_not_above_threshold` only with a valid limit. */
  amountCode: Exclude<tThresholdFieldCode, 'threshold_below_min'> | null;
  /**
   * The FIRST violation in wallet's precedence, over the pair: the code a PUT
   * of these two values would get back. Null when the pair passes, or when a
   * field is empty (there is nothing to send yet).
   */
  firstCode: tThresholdFieldCode | null;
};

const isWhole = (micros: bigint) => micros % MICROS_PER_UNIT === 0n;

function microsOf(amount: tThresholdAmount): bigint {
  return BigInt(amount.amountMicros);
}

/**
 * RC-18 over the limit and the amount, in wallet's order.
 *
 * Each field gets its own error, so the customer sees every field to fix at
 * once (a limit of 3 and an amount of 20 show both minimums). `firstCode` is
 * the one wallet would answer, and is what the precedence tests pin.
 */
export function validateThresholdValues(options: {
  threshold: tUnitsRead;
  amount: tUnitsRead;
  limits: tThresholdLimits;
}): tThresholdValidation {
  const { threshold, amount, limits } = options;

  let thresholdCode: tThresholdValidation['thresholdCode'] = null;
  if (threshold.status === 'invalid') thresholdCode = 'invalid_format';
  else if (threshold.status === 'ok') {
    if (!isWhole(threshold.micros)) thresholdCode = 'not_whole_units';
    else if (threshold.micros < microsOf(limits.thresholdMin)) {
      thresholdCode = 'threshold_below_min';
    }
  }

  let amountCode: tThresholdValidation['amountCode'] = null;
  if (amount.status === 'invalid') amountCode = 'invalid_format';
  else if (amount.status === 'ok') {
    if (!isWhole(amount.micros)) amountCode = 'not_whole_units';
    else if (amount.micros < microsOf(limits.amountMin)) {
      amountCode = 'amount_below_min';
    } else if (amount.micros > microsOf(limits.amountMax)) {
      amountCode = 'amount_above_max';
    } else if (
      threshold.status === 'ok' &&
      thresholdCode === null &&
      amount.micros <= threshold.micros
    ) {
      // Strictly greater (wallet): topping up exactly to the limit leaves the
      // balance on the edge, and the first message fires the next charge.
      amountCode = 'amount_not_above_threshold';
    }
  }

  return {
    thresholdCode,
    amountCode,
    firstCode: firstCodeOf({ threshold, amount, limits }),
  };
}

/** `ValidateThresholdTopUp`, line for line, over a complete pair. */
function firstCodeOf(options: {
  threshold: tUnitsRead;
  amount: tUnitsRead;
  limits: tThresholdLimits;
}): tThresholdFieldCode | null {
  const { threshold, amount, limits } = options;

  if (threshold.status === 'invalid' || amount.status === 'invalid') {
    return 'invalid_format';
  }
  if (threshold.status !== 'ok' || amount.status !== 'ok') return null;

  const th = threshold.micros;
  const am = amount.micros;

  if (!isWhole(th) || !isWhole(am)) return 'not_whole_units';
  if (th < microsOf(limits.thresholdMin)) return 'threshold_below_min';
  if (am < microsOf(limits.amountMin)) return 'amount_below_min';
  if (am > microsOf(limits.amountMax)) return 'amount_above_max';
  if (am <= th) return 'amount_not_above_threshold';

  return null;
}

/**
 * The figure each bound names, from the published limits. It is the same
 * value wallet puts in `limitMicros` for that code, so the server's 400 and
 * this check name the same figure.
 */
export function thresholdBoundLimitMicros(options: {
  code: tThresholdBoundCode;
  limits: tThresholdLimits;
  thresholdMicros: bigint | null;
}): string | null {
  const { code, limits, thresholdMicros } = options;

  switch (code) {
    case 'threshold_below_min':
      return limits.thresholdMin.amountMicros;
    case 'amount_below_min':
      return limits.amountMin.amountMicros;
    case 'amount_above_max':
      return limits.amountMax.amountMicros;
    case 'amount_not_above_threshold':
      return thresholdMicros === null ? null : thresholdMicros.toString();
    default:
      return null;
  }
}

const label = (options: {
  amountMicros: string;
  currency: tWalletCurrency;
  locale: string;
}) => formatWalletAmount({ ...options, trimZeroCents: true });

/**
 * The sentence of one bound, for the field it belongs to. ONE function for the
 * client check and for the server's 400 (U6), so the two cannot drift.
 *
 * `limitMicros` is the bound's figure (the 400 carries it; the client takes it
 * from `thresholdBoundLimitMicros`). Without a readable figure the sentence
 * cannot be written honestly, so it falls back to the generic amount text.
 */
export function thresholdBoundText(options: {
  code: tThresholdFieldCode;
  limitMicros: string | null;
  currency: tWalletCurrency;
  locale: string;
  t: tTranslate;
}): string {
  const { code, limitMicros, currency, locale, t } = options;

  if (code === 'not_whole_units') return t('topUp.amount.wholeOnly');
  if (code === 'invalid_format' || !limitMicros || !/^\d+$/.test(limitMicros)) {
    return t('topUp.errors.invalidAmount');
  }

  const figure = label({ amountMicros: limitMicros, currency, locale });

  switch (code) {
    case 'threshold_below_min':
      return t('topUp.threshold.errors.belowMinimum', { min: figure });
    case 'amount_below_min':
      return t('topUp.errors.belowMinimum', { min: figure });
    case 'amount_above_max':
      return t('topUp.threshold.errors.aboveMaximum', { max: figure });
    case 'amount_not_above_threshold':
      return t('topUp.threshold.errors.customNotAbove', { threshold: figure });
    default:
      return t('topUp.errors.invalidAmount');
  }
}

/** What the customer has in the form. `null` presetMicros = the free amount. */
export type tThresholdDraft = {
  threshold: string;
  presetMicros: string | null;
  custom: string;
};

/** A configuration in force: active, or paused (RET-2421), with both values. */
export function thresholdInForce(
  state: tThresholdTopUpState,
): { threshold: tThresholdAmount; amount: tThresholdAmount } | null {
  if (state.status === 'off' || !state.threshold || !state.amount) return null;

  return { threshold: state.threshold, amount: state.amount };
}

function unitsString(amount: tThresholdAmount): string {
  const micros = BigInt(amount.amountMicros);

  return isWhole(micros)
    ? (micros / MICROS_PER_UNIT).toString()
    : amount.amount;
}

/**
 * The values the form opens with (R2, RC-18, RC-21): the ones in force; else
 * the last ones the customer saved (`lastValues`); else wallet's defaults.
 * The amount selects its preset when one has that figure, else it goes to the
 * free amount.
 */
export function initialThresholdDraft(options: {
  state: tThresholdTopUpState;
  presets: tTopUpPreset[];
}): tThresholdDraft {
  const { state, presets } = options;
  const source = thresholdInForce(state) ??
    state.lastValues ?? {
      threshold: state.limits.thresholdDefault,
      amount: state.limits.amountDefault,
    };
  const amountMicros = BigInt(source.amount.amountMicros);
  const preset = presets.find(
    (candidate) => safeBigInt(candidate.amountMicros) === amountMicros,
  );

  return {
    threshold: unitsString(source.threshold),
    presetMicros: preset ? preset.amountMicros : null,
    custom: preset ? '' : unitsString(source.amount),
  };
}

function safeBigInt(value: string | null | undefined): bigint | null {
  if (!value || !/^-?\d+$/.test(value)) return null;

  return BigInt(value);
}

/** The threshold top-up in force, for the replace notices. */
export function activeThresholdOf(options: {
  state: tThresholdTopUpState;
  locale: string;
}): tActiveThreshold | null {
  const inForce = thresholdInForce(options.state);

  if (!inForce) return null;

  return {
    amountLabel: label({
      amountMicros: inForce.amount.amountMicros,
      currency: options.state.currency,
      locale: options.locale,
    }),
    thresholdLabel: label({
      amountMicros: inForce.threshold.amountMicros,
      currency: options.state.currency,
      locale: options.locale,
    }),
  };
}

/**
 * The footer line of a one-off top-up while the threshold top-up is paused
 * (RET-2426 U6, RC-21, P43), or null when it says nothing.
 *
 * It speaks only when wallet says a one-off paid with the paused card would end
 * the pause (`pause.oneOffResumes`): that is false for the daily cap, so the
 * cap's pause never gets the line. It never re-derives that rule from the
 * reason, a second copy of it.
 *
 * `chargesNow` is a PREDICTION and the only thing the front computes here: the
 * available balance plus the chosen amount, in micros, against the limit. Below
 * it, the threshold top-up charges right after the pause ends (RC-17). Every
 * input that is missing or unreadable (no balance, another currency, no
 * amount, no limit) leaves it false: the plain sentence promises less. With a
 * charge already in progress it is false too: the automatic one does not
 * charge on top of it. The notice after paying never reads this (KTD9).
 */
export function pausedThresholdOf(options: {
  state: tThresholdTopUpState;
  balance: tWalletBalance | null;
  /** What this click would charge, in micros; null when there is no valid amount. */
  amountMicros: string | null;
  locale: string;
}): tPausedThreshold | null {
  const { state, balance, amountMicros, locale } = options;

  if (!state.pause || state.pause.oneOffResumes !== true) return null;

  const thresholdMicros = safeBigInt(state.threshold?.amountMicros);
  const chosenMicros = safeBigInt(amountMicros);
  const balanceMicros =
    balance && balance.currency === state.currency
      ? safeBigInt(balance.amountMicros)
      : null;

  const chargesNow =
    state.amount !== null &&
    state.chargeInProgress === null &&
    thresholdMicros !== null &&
    chosenMicros !== null &&
    balanceMicros !== null &&
    balanceMicros + chosenMicros < thresholdMicros;

  return {
    amountLabel: state.amount
      ? label({
          amountMicros: state.amount.amountMicros,
          currency: state.currency,
          locale,
        })
      : '',
    thresholdLabel: state.threshold
      ? label({
          amountMicros: state.threshold.amountMicros,
          currency: state.currency,
          locale,
        })
      : '',
    chargesNow,
  };
}

const KNOWN_BLOCKED_BY: readonly tThresholdBlockedBy[] = [
  'past_due',
  'credit_package_scheduled',
];

/**
 * Whether a `typeChange.blockedBy` is one this front can name. The one list of
 * known reasons: the load effect reports the rest, and `typeBlockedOf` treats
 * the rest as allowed.
 */
export function isKnownBlockedBy(value: string): value is tThresholdBlockedBy {
  return KNOWN_BLOCKED_BY.some((known) => known === value);
}

/**
 * RC-22: which automatic type cannot be chosen now, and why.
 *
 * The blocked type is the OTHER one: with a threshold top-up in force it is
 * the recurring one; with a recurring one (or a converted credit package) it
 * is the threshold one. With neither in force there is no type to change from.
 *
 * A `blockedBy` this front does not know is treated as ALLOWED (null); the
 * container's load effect reports it (`isKnownBlockedBy`). The PUT and the
 * POST check again and answer 409, so letting the choice through costs one
 * round trip, while blocking on a reason nobody can name would be a dead end
 * with no text.
 */
export function typeBlockedOf(options: {
  state: tThresholdTopUpState;
  hasActiveRecurring: boolean;
  locale: string;
  formatDate: (rfc3339: string | undefined, locale: string) => string;
}): tTopUpTypeBlocked | null {
  const { state, hasActiveRecurring, locale, formatDate } = options;
  const blockedBy = state.typeChange.blockedBy;

  if (!blockedBy || !isKnownBlockedBy(blockedBy)) return null;

  const kind = thresholdInForce(state)
    ? ('recurring' as const)
    : hasActiveRecurring
      ? ('threshold' as const)
      : null;

  if (!kind) return null;

  return {
    kind,
    reason: blockedBy,
    dateLabel: formatDate(
      state.typeChange.scheduledChangeOn ?? undefined,
      locale,
    ),
  };
}

/** Everything the container hands the component for `kind === 'threshold'`. */
export type tThresholdForm = {
  info: Omit<tThresholdInfo, 'onChange'>;
  selectedPresetMicros: string | null;
  customAmount: string;
  totalLabel: string;
  customAmountError: string | null;
  /** The total, only when this click could save it (R6). */
  chargeAmountLabel: string;
  /** The pair passes every bound: a PUT of these values is valid. */
  isValid: boolean;
  thresholdMicros: bigint | null;
  amountMicros: bigint | null;
};

/**
 * Builds the form's props from the GET state, the presets, what the customer
 * has typed and the balance the wallet provider holds.
 */
export function buildThresholdForm(options: {
  state: tThresholdTopUpState;
  presets: tTopUpPreset[];
  draft: tThresholdDraft;
  balance: tWalletBalance | null;
  locale: string;
  t: tTranslate;
}): tThresholdForm {
  const { state, presets, draft, balance, locale, t } = options;
  const { currency, limits } = state;

  const selectedPreset =
    draft.presetMicros === null
      ? null
      : (presets.find((preset) => preset.amountMicros === draft.presetMicros) ??
        null);
  const usesCustom = selectedPreset === null;

  const thresholdRead = readUnits(draft.threshold);
  const amountRead: tUnitsRead = selectedPreset
    ? (() => {
        const micros = safeBigInt(selectedPreset.amountMicros);

        return micros === null
          ? { status: 'invalid' as const }
          : { status: 'ok' as const, micros };
      })()
    : readUnits(draft.custom);

  const validation = validateThresholdValues({
    threshold: thresholdRead,
    amount: amountRead,
    limits,
  });

  const thresholdMicros =
    thresholdRead.status === 'ok' && validation.thresholdCode === null
      ? thresholdRead.micros
      : null;
  const amountMicros =
    amountRead.status === 'ok' && validation.amountCode === null
      ? amountRead.micros
      : null;

  const textOf = (code: tThresholdFieldCode) =>
    thresholdBoundText({
      code,
      limitMicros:
        code === 'invalid_format' || code === 'not_whole_units'
          ? null
          : thresholdBoundLimitMicros({
              code,
              limits,
              thresholdMicros:
                thresholdRead.status === 'ok' ? thresholdRead.micros : null,
            }),
      currency,
      locale,
      t,
    });

  const thresholdError = validation.thresholdCode
    ? textOf(validation.thresholdCode)
    : null;
  const thresholdLabel =
    thresholdMicros === null
      ? ''
      : label({ amountMicros: thresholdMicros.toString(), currency, locale });

  // A preset that breaks a bound stays selectable and shows red (RC-18, R4):
  // its sentence takes the help line's place as an error.
  const presetInvalid = !usesCustom && validation.amountCode !== null;
  const someNotAbove =
    thresholdMicros !== null &&
    presets.some((preset) => {
      const micros = safeBigInt(preset.amountMicros);

      return micros !== null && micros <= thresholdMicros;
    });
  const amountHint = presetInvalid
    ? validation.amountCode === 'amount_not_above_threshold'
      ? t('topUp.threshold.amountHint', { threshold: thresholdLabel })
      : textOf(validation.amountCode as tThresholdFieldCode)
    : someNotAbove
      ? t('topUp.threshold.amountHint', { threshold: thresholdLabel })
      : null;

  const customAmountError =
    usesCustom && validation.amountCode ? textOf(validation.amountCode) : null;

  // The footer headline. A preset reads its own label. A free amount reads
  // only when it passes every amount bound, formatted like the presets: an
  // invalid one (not above the limit, below the minimum, above the maximum)
  // is not summarized, exactly like an invalid preset (prototype states
  // monto-no-mayor, minimos, monto-maximo).
  const totalLabel = selectedPreset
    ? selectedPreset.label
    : amountRead.status === 'ok' && validation.amountCode === null
      ? label({
          amountMicros: amountRead.micros.toString(),
          currency,
          locale,
        })
      : '';

  const isValid =
    thresholdMicros !== null &&
    amountMicros !== null &&
    validation.firstCode === null;

  const inForce = thresholdInForce(state);
  const unchanged =
    inForce !== null &&
    thresholdMicros !== null &&
    amountMicros !== null &&
    thresholdMicros === microsOf(inForce.threshold) &&
    amountMicros === microsOf(inForce.amount);

  const inProgressLabel = state.chargeInProgress
    ? label({
        amountMicros: state.chargeInProgress.amount.amountMicros,
        currency,
        locale,
      })
    : null;

  // KTD8: a prediction. Wallet decides the charge with its own balance; the
  // success notice reads the PUT's `charge`, not this.
  const balanceMicros =
    balance && balance.currency === currency
      ? safeBigInt(balance.amountMicros)
      : null;
  const belowBalanceLabel =
    isValid &&
    !state.chargeInProgress &&
    balance !== null &&
    balanceMicros !== null &&
    thresholdMicros !== null &&
    balanceMicros < thresholdMicros
      ? formatWalletAmount({
          amountMicros: balance.amountMicros,
          currency,
          locale,
        })
      : null;

  return {
    info: {
      value: draft.threshold,
      thresholdLabel,
      minLabel: label({
        amountMicros: limits.thresholdMin.amountMicros,
        currency,
        locale,
      }),
      error: thresholdError,
      amountHint,
      amountInvalid: presetInvalid,
      dailyMax: limits.dailyMax > 0 ? limits.dailyMax : null,
      unchanged,
      belowBalanceLabel,
      inProgressLabel,
    },
    selectedPresetMicros: selectedPreset ? selectedPreset.amountMicros : null,
    customAmount: usesCustom ? draft.custom : '',
    totalLabel,
    customAmountError,
    chargeAmountLabel: isValid ? totalLabel : '',
    isValid,
    thresholdMicros,
    amountMicros,
  };
}
